import React, { useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  useGetAdminConversationsQuery,
  useAdminDeleteConversationMutation,
} from "../../../../store/slices/api";
import { SharedLoading, SharedFeatureError } from "../../../shared";
import { failedMessage } from "../../../shared/serverReason";

dayjs.extend(utc);
dayjs.extend(timezone);

function ConversationRow({ conversation, individualId }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConversation, { isLoading: isDeleting }] =
    useAdminDeleteConversationMutation();
  const [deleteError, setDeleteError] = useState(null);

  const handleDelete = async () => {
    setDeleteError(null);
    try {
      await deleteConversation({ id: conversation.id, individual_id: individualId }).unwrap();
      // The list stays up while it refreshes, so close the prompt rather than
      // offer "Yes, Delete" again until the row drops out.
      setShowDeleteConfirm(false);
    } catch (err) {
      setDeleteError(failedMessage(err, "The conversation wasn't deleted. Please try again."));
    }
  };

  const formattedDate = conversation.last_message_at
    ? dayjs(conversation.last_message_at).utc().local().format("MMM D, YYYY h:mma")
    : "No messages";

  const createdDate = dayjs(conversation.created_at)
    .utc()
    .local()
    .format("MMM D, YYYY");

  return (
    <div className="flex flex-col rounded-[16px] shadow-md p-[16px] border border-dupontGold/30 dark:border-darkGold/20 bg-white dark:bg-darkCard gap-y-[8px]">
      <div className="flex flex-row justify-between items-start">
        <div className="flex flex-col gap-y-[4px]">
          <div className="text-[17px] font-bold text-dupontBlue dark:text-darkText">
            {conversation.subject}
          </div>
          <div className="text-[13px] text-dupontGray dark:text-darkTextSecondary">
            {conversation.participants.join(", ")}
          </div>
        </div>
        <div className="flex flex-col items-end gap-y-[2px]">
          <span className="text-[11px] font-bold text-dupontGoldDk dark:text-darkGold bg-dupontCream dark:bg-darkGold/20 px-[8px] py-[2px] rounded-full">
            {conversation.message_count} message{conversation.message_count !== 1 ? "s" : ""}
          </span>
        </div>
      </div>
      <div className="text-[13px] text-dupontGray dark:text-darkTextSecondary">
        Last activity: {formattedDate} &middot; Created: {createdDate}
      </div>
      <div className="flex flex-row gap-x-[10px] pt-[8px] border-t border-dupontGold/30 dark:border-darkGold/20 items-center">
        {showDeleteConfirm ? (
          <div className="flex flex-row flex-wrap items-center gap-x-[10px]">
            <span className="text-[14px] text-red-600">
              Delete this conversation and all its messages?
            </span>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="text-[13px] font-bold text-red-600 hover:text-red-800 disabled:opacity-50"
            >
              {isDeleting ? "Deleting..." : "Yes, Delete"}
            </button>
            <button
              onClick={() => {
                setDeleteError(null);
                setShowDeleteConfirm(false);
              }}
              className="text-[13px] font-bold text-dupontGray hover:text-dupontBlue"
            >
              Cancel
            </button>
            {deleteError && (
              <span role="alert" className="basis-full text-[14px] font-body text-red-700 dark:text-red-400">
                {deleteError}
              </span>
            )}
          </div>
        ) : (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="text-[13px] font-bold text-red-400 hover:text-red-600"
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

function CorrespondenceManagement({ individualId }) {
  const { data: conversations, isLoading, isFetching, isError, refetch } =
    useGetAdminConversationsQuery(individualId);

  // Also while "Try again" runs: RTK Query then reports neither loading nor
  // error and no data, which would leave the tab blank.
  if (isLoading || (!conversations && isFetching)) {
    return <SharedLoading text="Loading conversations..." />;
  }

  // A failed load says so, with a retry.
  if (isError && !conversations) {
    return <SharedFeatureError title="We couldn't load the conversations" onRetry={refetch} />;
  }

  if (!conversations) {
    return null;
  }

  if (conversations.length === 0) {
    return (
      <div className="text-[15px] text-dupontGray dark:text-darkTextSecondary">
        No conversations yet.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-[16px]">
      <div className="text-[14px] text-dupontGray dark:text-darkTextSecondary">
        {conversations.length} conversation{conversations.length !== 1 ? "s" : ""} total
      </div>
      {conversations.map((convo) => (
        <ConversationRow
          key={convo.id}
          conversation={convo}
          individualId={individualId}
        />
      ))}
    </div>
  );
}

export default CorrespondenceManagement;
